import { environment } from 'src/environments/environment';

/** Datos de geolocalización por IP (vía proxy CMS → ipapi). */
export interface UserGeolocation {
  ip?: string;
  country?: string;
  country_code?: string;
  continent_code?: string;
  city?: string;
  region?: string;
  region_code?: string;
  timezone?: string;
  isp?: string;
  latitude?: number;
  longitude?: number;
}

const GEO_STORAGE_KEY = 'cicerone_ipapi_geo_cache';
/** Cada consulta nueva es un crédito de ipapi: la geo se reutiliza 24 h entre pestañas y visitas. */
const GEO_TTL_MS = 24 * 60 * 60 * 1000;
/**
 * Crawlers, monitores y previsualizadores de enlaces: no son visitas y no deben gastar créditos.
 * `(?<!cu)bot`: los móviles Cubot ("CUBOT X30") son navegadores reales.
 */
const BOT_USER_AGENT = /(?<!cu)bot|crawl|spider|slurp|headless|lighthouse|pagespeed|facebookexternalhit|whatsapp|preview|uptime|pingdom/i;

let cachedGeolocation: UserGeolocation | null = null;
let inflightRequest: Promise<UserGeolocation | null> | null = null;

function normalizeGeo(data: Record<string, unknown> | null): UserGeolocation | null {
  if (!data)
    return null;

  return {
    ip: (data.ip as string) || undefined,
    country: (data.country as string) || (data.country_name as string) || undefined,
    country_code: (data.country_code as string) || undefined,
    continent_code: (data.continent_code as string) || undefined,
    city: (data.city as string) || undefined,
    region: (data.region as string) || undefined,
    region_code: (data.region_code as string) || undefined,
    timezone: (data.timezone as string) || undefined,
    isp: (data.isp as string) || (data.org as string) || undefined,
    latitude: typeof data.latitude === 'number' ? data.latitude : undefined,
    longitude: typeof data.longitude === 'number' ? data.longitude : undefined,
  };
}

/** Cualquier geo usable (IP basta): evita reintentar ipapi si falta city/country. */
function hasUsableGeo(geo: UserGeolocation | null): geo is UserGeolocation {
  return !!geo && !!(geo.ip || geo.city || geo.country || geo.country_code);
}

/** Navegador automatizado (webdriver) o user agent de bot: no se pide la geo. */
function isAutomatedBrowser(): boolean {
  if (typeof navigator === 'undefined')
    return true;

  return navigator.webdriver === true || BOT_USER_AGENT.test(navigator.userAgent || '');
}

function readStoredGeo(): UserGeolocation | null {
  try {
    if (typeof localStorage === 'undefined')
      return null;
    const raw = localStorage.getItem(GEO_STORAGE_KEY);
    if (!raw)
      return null;

    const stored = JSON.parse(raw);
    if (!stored?.expiresAt || stored.expiresAt < Date.now()) {
      localStorage.removeItem(GEO_STORAGE_KEY);
      return null;
    }
    return normalizeGeo(stored.geo);
  } catch {
    return null;
  }
}

function writeStoredGeo(geo: UserGeolocation): void {
  try {
    if (typeof localStorage === 'undefined')
      return;
    localStorage.setItem(GEO_STORAGE_KEY, JSON.stringify({ geo, expiresAt: Date.now() + GEO_TTL_MS }));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Proxy CMS: la API key de ipapi vive solo en el .env del backend. */
async function fetchFromCmsProxy(): Promise<UserGeolocation | null> {
  const base = environment.apiUrlBase?.replace(/\/$/, '') || 'https://cicerone.cms-iurban.com';
  const response = await fetch(`${base}/api/ip-geolocation`);
  if (!response.ok)
    return null;

  const data = await response.json();
  if (data?.error)
    return null;

  return normalizeGeo(data);
}

export async function fetchGeolocation(): Promise<UserGeolocation | null> {
  return fetchFromCmsProxy();
}

/**
 * Una sola consulta por navegador y día (memoria + localStorage con caducidad de 24 h).
 * Prefetch / trackEntry / rebind deben reutilizar este resultado; no re-llamar ipapi.
 * Los bots no consultan.
 */
export async function getCachedGeolocation(): Promise<UserGeolocation | null> {
  if (hasUsableGeo(cachedGeolocation))
    return cachedGeolocation;

  const fromStorage = readStoredGeo();
  if (hasUsableGeo(fromStorage)) {
    cachedGeolocation = fromStorage;
    return fromStorage;
  }

  if (isAutomatedBrowser())
    return null;

  if (!inflightRequest) {
    inflightRequest = fetchGeolocation().finally(() => {
      inflightRequest = null;
    });
  }

  const result = await inflightRequest;
  if (hasUsableGeo(result)) {
    cachedGeolocation = result;
    writeStoredGeo(result);
  }

  return result;
}

export function getFormGeolocation(): UserGeolocation | null {
  return cachedGeolocation ?? readStoredGeo();
}

export function clearGeolocationCache(): void {
  cachedGeolocation = null;
  try {
    if (typeof localStorage !== 'undefined')
      localStorage.removeItem(GEO_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
